import PreviewSvg from '@assets/images/icons/preview.svg'
import type { TooltipProps } from '@mui/material'
import { Tooltip, tooltipClasses } from '@mui/material'
import { styled } from '@mui/material/styles'
import type { PropsWithChildren, ReactElement } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

const TooltipText = styled('p')({
  fontSize: '0.8rem',
  margin: '0',
  padding: '0',
  color: 'var(--primary-color)'
})

const ImageTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: '600px',
    height: '300px',
    padding: '12px',
    backgroundColor: 'var(--image-tooltip-bg-color)',
    display: 'flex'
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'var(--image-tooltip-bg-color)'
  }
})

const TextTooltip = styled(({ className, ...props }: TooltipProps & { isAdmin?: boolean }) => (
  <Tooltip {...props} classes={{ popper: className }} />
), {
  shouldForwardProp: prop => prop !== 'isAdmin'
})<{ isAdmin?: boolean }>(({ isAdmin }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'var(--tooltip-bg-color)',
    opacity: 'var(--layer-upper-opacity)',
    zIndex: 100000,
    maxWidth: '400px',
    ...(isAdmin ? { backgroundColor: 'var(--image-tooltip-bg-color)' } : {})
  }
}))

const PreviewIcon = styled('img')({
  filter: 'var(--logo-light)',
  width: '2.5rem',
  height: '2.5rem',
  margin: 'var(--marginDenseTop) 0 0 0',
  cursor: 'pointer'
})

type YisToolTipProps = PropsWithChildren
  & Partial<TooltipProps> & {
    text?: string
    image?: string
    altImage?: string
    placement?: string
    iconClassName?: string
    isAdmin?: boolean
  }

export default function YisToolTip (props: YisToolTipProps) {
  const { children, text, iconClassName, image, altImage, placement, isAdmin, ...rest } = props
  const { t } = useTranslation()

  if (image) {
    return <ImageTooltip
      disableInteractive
      title={
        <React.Fragment>
          <img src={image} alt={altImage || 'tooltip_image'} />
        </React.Fragment>
      }
      arrow
      placement={placement || 'bottom-end'}
    >
      <PreviewIcon
        src={PreviewSvg}
        alt="preview"
        className={iconClassName}
      />
    </ImageTooltip>
  }

  return <TextTooltip
    disableInteractive
    title={<TooltipText>{t(text)}</TooltipText>}
    isAdmin={isAdmin}
    placement={placement || 'top'}
    {...rest}
  >
    {children as ReactElement}
  </TextTooltip>
}
